import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Command, LogOut, Settings, LayoutDashboard, Users, Sparkles, UserPlus, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePro } from "@/contexts/ProContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguagePicker } from "@/components/LanguagePicker";
import { PrivacyToggle } from "@/components/PrivacyToggle";
import { ProBadge } from "@/components/ProBanner";
import { BottomNav } from "@/components/BottomNav";

export const Navbar = ({ onOpenCmdK }) => {
  const { user, logout } = useAuth();
  const { isPro } = usePro();
  const { t } = useTranslation();
  const loc = useLocation();
  const nav = useNavigate();

  const link = (to, label, Icon, testId) => {
    const active = loc.pathname === to || (to !== "/dashboard" && to !== "/pro-dashboard" && loc.pathname.startsWith(to));
    const exactActive = to === loc.pathname;
    const isActive = exactActive || active;
    return (
      <Link
        to={to}
        className={`flex items-center gap-2 px-3 h-9 rounded-md text-sm transition whitespace-nowrap ${
          isActive ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
        }`}
        data-testid={testId || `nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        <span>{label}</span>
      </Link>
    );
  };

  if (!user) return null;

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/70 backdrop-blur-xl">
      <div className="px-3 sm:px-10 h-16 flex items-center justify-between gap-2">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/dashboard" className="flex items-center gap-2 font-heading font-bold shrink-0" data-testid="navbar-logo">
            <span className="h-5 w-5 rounded-md brand-bg" />
            <span className="hidden sm:inline">Split the <span className="brand-text">____</span></span>
          </Link>
          <nav className="flex items-center gap-1 min-w-0 overflow-x-auto scrollbar-thin">
            {link("/dashboard", t("nav.dashboard"), LayoutDashboard, "nav-dashboard")}
            {link("/groups", t("nav.groups"), Users, "nav-groups")}
            {link("/friends", t("nav.friends", "Friends"), UserPlus, "nav-friends")}
            {link("/pro-dashboard", t("nav.pro_dashboard"), Sparkles, "nav-pro-dashboard")}
          </nav>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm" onClick={onOpenCmdK}
            className="hidden md:flex items-center gap-2 text-muted-foreground hover:text-foreground text-xs font-mono border border-border bg-foreground/[0.02] h-9"
            data-testid="navbar-cmdk-btn"
          >
            <Command className="h-3.5 w-3.5" strokeWidth={1.5} /> <span>K</span>
          </Button>
          <LanguagePicker />
          <PrivacyToggle />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 flex items-center gap-2 px-1.5 rounded-full bg-foreground/5 hover:bg-foreground/10 border border-border transition" data-testid="navbar-avatar-btn">
                <span className="h-6 w-6 rounded-full flex items-center justify-center text-base">{user.avatar_emoji || "🙂"}</span>
                {isPro && <ProBadge />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 bg-card border-border">
              <DropdownMenuLabel className="font-normal">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">{user.name}</div>
                  {isPro && <ProBadge />}
                </div>
                <div className="text-xs text-muted-foreground font-mono">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem onClick={() => nav("/pro-dashboard")} data-testid="menu-pro" className="cursor-pointer">
                <Sparkles className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {t("nav.pro_dashboard")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings" className="cursor-pointer">
                <Settings className="h-4 w-4 mr-2" strokeWidth={1.5} /> {t("nav.settings")}
              </DropdownMenuItem>
              {user.is_admin && (
                <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin" className="cursor-pointer">
                  <Shield className="h-4 w-4 mr-2 brand-text" strokeWidth={1.5} /> Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => { logout(); nav("/"); }}
                data-testid="menu-logout"
                className="cursor-pointer text-red-400 focus:text-red-300"
              >
                <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <BottomNav />
    </header>
  );
};
